import * as React from 'react';
import { useSelector } from 'react-redux';

import { useMessagesRead, useMessagesScroll } from './hooks';
import { useMessagesPages } from './hooks/useMessagesPages';
import { ChatMessageChatbotTyping } from '../../components/ChatMessageChatbotTyping';
import { ChatScrollLayout } from '../../components/ChatScrollLayout';
import { ChatUnreadMessagesButton } from '../../components/ChatUnreadMessagesButton';
import { ScrollButton } from '../../components/ScrollButton';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { chatbotSelector, currentOpenChatSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { noop } from '../../utils/noop';
import { ChatArchiveInfoContainer } from '../ChatArchiveInfoContainer';
import { ChatMessageMenuProvider, ChatMessageMenuContainer } from '../ChatMessageMenuContainer';
import { ChatPageContainer } from '../ChatPageContainer';
import { ChatRemovalDateContainer } from '../ChatRemovalDateContainer';
import { ChatbotButtonsContainer } from '../ChatbotButtonsContainer';
import { OnboardingButtonContainer } from '../OnboardingButtonContainer/OnboardingButtonContainer';
import { IScrollContainerHandle, ScrollContainer } from '../ScrollContainer';
import { ReplyOnboardingProvider } from '../../containers/ReplyOnboardingProvider';
import { ScrollToMessageLoaderContainer } from '../ScrollToMessageLoaderContainer';

interface IChatScrollContainerProps {
  scrollRef: React.RefObject<IScrollContainerHandle | null>;
  rootRef: React.RefObject<HTMLDivElement | null>;
  scrollToBottom: (isButtonClickScroll?: boolean) => void;
}

export const ChatScrollContainer: React.FC<IChatScrollContainerProps> = ({ scrollRef, rootRef, scrollToBottom }) => {
  const unreadRef = React.useRef<HTMLDivElement | null>(null);
  const [isScrollToBottomButtonVisible, setIsScrollToBottomButtonVisible] = React.useState<boolean>(false);

  const currentChat = useSelector(currentOpenChatSelector);
  const { isTyping, onboardingButtons } = useSelector(chatbotSelector);

  const isLoading = currentChat?.status === ERequestApiStatus.Pending;

  const { unreadMessagesCount, setRefInMessagesMap, markMessagesAsRead, getFirstUnreadMessage } = useMessagesRead();
  const { getNextMessagesPage } = useMessagesScroll(scrollToBottom, getFirstUnreadMessage, unreadRef);

  const messagesLength = React.useMemo(() => {
    return currentChat?.chatMessages?.length || 0;
  }, [currentChat]);

  const pages = useMessagesPages(currentChat?.chatMessages || []);

  const isChatbotChat = currentChat?.chatId === CHATBOT_ID;

  React.useEffect(() => {
    markMessagesAsRead();
  }, [markMessagesAsRead, messagesLength, currentChat]);

  const pagesNode = React.useMemo(() => {
    const nodes = pages.map(page => (
      <ChatPageContainer
        key={`${currentChat?.chatId}-${page.date}`}
        page={page}
        setRefInMessagesMap={setRefInMessagesMap}
        unreadRef={unreadRef}
      />
    ));

    if (nodes && isTyping) {
      nodes.push(<ChatMessageChatbotTyping key="typing-message" />);
    }

    return nodes;
  }, [pages, setRefInMessagesMap, isTyping, currentChat?.chatId]);

  return (
    <ChatMessageMenuProvider>
      <ReplyOnboardingProvider>
        <ScrollContainer
          ref={scrollRef}
          showScrollBottomButton
          onButtonVisible={setIsScrollToBottomButtonVisible}
          onScroll={isChatbotChat ? noop : getNextMessagesPage}
        >
          <ChatScrollLayout
            rootRef={rootRef}
            isLoading={isLoading}
            hasButtonSeparator={onboardingButtons.length === 0}
            pagesNode={pagesNode}
            chatbotOnboardingNode={<OnboardingButtonContainer />}
            chatbotButtonsNode={<ChatbotButtonsContainer />}
            removalDateNode={<ChatRemovalDateContainer />}
            archiveInfoNode={<ChatArchiveInfoContainer />}
          />
        </ScrollContainer>
        {unreadMessagesCount > 0 && (
          <ChatUnreadMessagesButton counter={unreadMessagesCount} onClick={() => scrollToBottom(true)} />
        )}
        {isScrollToBottomButtonVisible && <ScrollButton onClick={() => scrollToBottom(true)} />}
        <ChatMessageMenuContainer />
        <ScrollToMessageLoaderContainer />
      </ReplyOnboardingProvider>
    </ChatMessageMenuProvider>
  );
};
